/**
 * The /v1 transport contract for the core model: the template catalogue,
 * onboarding apply, and the schema read API. Stages 2 and 3 of
 * docs/plans/done/core-model.md. Transport shapes only, the same rule as auth.ts:
 * no business logic lives here.
 *
 * Extensionless relative imports, for the same reason index.ts gives: this
 * package is consumed as TypeScript source by every app, and apps/mobile does
 * not set allowImportingTsExtensions.
 */
import type { RecordCapability } from './auth';
import type { ChoiceFilter, FieldSpec, FormulaSpec, LinesFieldSpec } from './formula';
import type { ComponentId } from './generated/component-ids';
import type { PurposeId } from './generated/purposes';
import type { RecordLayout } from './layout';
import type { ValueTypeId } from './generated/value-types';
import type { EntryReaches } from './roles';

/**
 * POST /v1/onboarding/apply
 *
 * Auth required, and it refuses a request that carries x-alluzo-tenant: this
 * is the one route that creates the tenant it is about, so a header naming
 * one is a confused caller. The no-AI fallback: a name, a currency, and the
 * first blank record. New businesses with a model key use compose instead.
 */
export type OnboardingApplyRequest = {
  businessName: string;
  /** Asked at creation, never inferred, per D12. ISO 4217, such as INR. */
  currency: string;
  /**
   * The first record. A business with no records is refused. The server builds
   * the same `blankSpec` the home flow builds.
   */
  blank: { name: string; counts: RecordBlankCounts };
};

/**
 * What a record is, mirroring app.record_kind. A `record` holds the entries a
 * person saves, a `directory` holds the people or things other records point
 * at, and a `lines` record holds the lines of one entry: an invoice's items, a
 * stay's rooms. The server makes a lines record beside the `lines` field that
 * points at it, and it never shows in the record list, on home or in a picker.
 */
export const RECORD_KINDS = ['record', 'directory', 'lines'] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/**
 * The kinds a person sees and makes. Every list, picker, proposal and setup
 * draft names one of these, never `lines`: a lines record is the server's,
 * reached only through the entry that owns it.
 */
export type VisibleRecordKind = Exclude<RecordKind, 'lines'>;

/** One record onboarding created. The home screen lists these. */
export type OnboardedRecord = {
  id: string;
  name: string;
  kind: VisibleRecordKind;
  /**
   * Null on a record nobody's template made: the person named it themselves
   * and picked its fields. `app.record.template_id` has always been nullable;
   * this type simply stops pretending otherwise.
   */
  templateId: string | null;
  nounKey: string;
  entryNounKey: string;
};

export type OnboardingApplyResponse = {
  tenantId: string;
  records: OnboardedRecord[];
};

/**
 * Which way one record moves money, read off the `sign` purpose.
 *
 * A constant answers every row, so the record itself points one way. A field
 * answers per row, so the record points both ways and only its rows know
 * which. A record with no measure and no sign moves nothing.
 */
export type RecordDirection = 'in' | 'out' | 'mixed';

/**
 * What the CALLER may do in one record or list, sent so the device can draw a
 * screen that agrees with the server.
 *
 * A COURTESY, NEVER A FENCE. The server refuses regardless, and it is the only
 * thing that does. Every use of this on a device hides or relabels a control;
 * no screen may skip a request because the local copy said no.
 *
 * `field_policy` is deliberately absent: it is already applied to the schema
 * before it leaves, so sending it would let a device disagree with a payload
 * it has already been given. `writableFields` is NOT applied to the schema —
 * a narrowed member still sees every field — so it travels here, and the new
 * entry form leaves the fields outside the family undrawn rather than offer a
 * save the server will refuse (FIELD_NOT_WRITABLE), which offline would be a
 * 4xx and a dropped outbox row.
 */
export type RecordGrantSummary = {
  capabilities: RecordCapability[];
  /** `own_entries` means the rows of other people never arrive at all. */
  rowScope: 'all' | 'own_entries';
  /** `submit_draft` and `request` change what the save button SAYS. */
  writeMode: 'direct' | 'submit_draft' | 'request';
  balanceVisible: boolean;
  direction: 'in_only' | 'out_only' | null;
  /** The field ids this person may write. Null — or, on the wire, absent — means every field. */
  writableFields: string[] | null;
  /**
   * Whose entries each entry permission reaches: Off, Own, Their team or
   * Everyone (web-redesign-02, phase 3.2). `rowScope` above is kept for a
   * phone already in people's hands, and says `all` for Their team, because the
   * server has already cut the rows to the team: an old phone that does not
   * know `reach` never prunes a row it may see. Absent from a summary an older
   * server sent.
   */
  reach?: EntryReaches;
  /**
   * The authors Their team covers, the person first: sent only when `reach.see`
   * is Their team (web-redesign-04 phase 3). The phone keeps those people's
   * rows and prunes the rest. Absent from an older server's summary, and then
   * a phone keeps every row it holds.
   */
  team?: string[];
  /**
   * A digest of the fields hidden from this person in this record, absent when
   * none is. Only ever compared: when it moves, the phone's schema and rows for
   * the record were cut to an older mask and are read again.
   */
  maskKey?: string;
};

/**
 * What one record HOLDS, in one word, so its glyph can say so.
 *
 * Every list on the home screen wears the same grey glyph today, so Staff,
 * Locations and Payment modes look like one thing. A record knows what it
 * holds; this is the vocabulary it says it in.
 *
 * A closed list of NAMES and not of icon files, for the reason every other
 * closed list here is one: the record says `people` and the device decides
 * which icon of its own design system that draws, so the same record wears the
 * family glyph on both platforms and a new icon set is a change on one side.
 * Twenty-three words, wide enough for the lists a shop keeps and narrow enough
 * that a person picks from a screen rather than searches.
 *
 * It is stored in `app.record.prefs` under `icon`, beside `capture`.
 */
export const RECORD_ICON_IDS = [
  'people',
  'person',
  'places',
  'buildings',
  'trips',
  'things',
  'stock',
  'shopping',
  'money',
  'payments',
  'bank',
  'categories',
  'work',
  'tasks',
  'documents',
  'health',
  'tools',
  'devices',
  'power',
  'water',
  'calendar',
  'book',
  'list',
] as const;

export type RecordIconId = (typeof RECORD_ICON_IDS)[number];

export const isRecordIconId = (value: unknown): value is RecordIconId =>
  typeof value === 'string' && (RECORD_ICON_IDS as readonly string[]).includes(value);

/** One record as the home screen draws it. */
export type RecordListItem = {
  id: string;
  name: string;
  kind: VisibleRecordKind;
  nounKey: string;
  entryNounKey: string;
  currency: string | null;
  templateId: string | null;
  /** Null when the record measures nothing. A directory always is. */
  direction: RecordDirection | null;
  /**
   * What this record holds, as the glyph should draw it. The EFFECTIVE answer,
   * not the stored one: a record that was never given an icon is answered the
   * default here, so the catalog never has to work one out. A directory with a
   * live phone field is `people`, any other directory is `list`, and a record
   * is null and keeps the direction glyph it already draws.
   *
   * Optional, and absent means exactly what null means. `toRecordListItem` is
   * the one assembly of this row and it always answers the key, so nothing on
   * the wire is ever without it; the option is for the mirror and the fixtures
   * that shape a catalog row to test something else entirely. Read
   * `icon ?? null`, exactly as `capture` is read.
   */
  icon?: RecordIconId | null;
  /** Live rows only. A tombstoned entry is not counted. */
  entryCount: number;
  /** The `record_headline` aggregate, signed. Null when there is no measure. */
  headlineValue: number | null;
  /** When an entry was last written here, ISO 8601. Null when there are none. */
  lastEntryAt: string | null;
  /**
   * What the caller may do in this record or list. Null for an owner, who holds
   * everything without a grant row — so "owner" is one check on the device and
   * not a comparison of eight strings.
   *
   * A device that reads null must treat it as PERMISSIVE. Null is also what an
   * older payload leaves behind, and hiding a button on missing data would lock
   * a person out of their own records after an upgrade, offline.
   */
  grant: RecordGrantSummary | null;
  /**
   * The person's own word for one entry, set in Customize. Null means the
   * record still says `entryNounKey`. Read `entryNoun ?? null`: the key is
   * optional for the fixtures, and every server answer carries it.
   */
  entryNoun?: RecordEntryNoun | null;
  /**
   * Who can use this record: every primary admin, and everyone holding access
   * to it. The People filter and the avatars on Records home read it.
   *
   * GET /records answers it; the sync pull does not, so a row off the mirror
   * is without it. Absent means "not asked", never "nobody".
   */
  people?: RecordPerson[];
  /**
   * This month's money in and out, summed per entry by the same sign rule the
   * day's totals use, in `currency`. Null when this caller may not see the
   * amount (a hidden measure, or the balance withheld) or the record holds no
   * money. GET /records only, like `people`.
   */
  inThisMonth?: number | null;
  outThisMonth?: number | null;
  /**
   * A list only: the records that pick from it through a live field, among
   * the records this caller may open. Empty on a list nothing picks from, and
   * absent on a record. GET /records only.
   */
  usedBy?: string[];
  /**
   * Who wrote the latest live entry here, the "who" of "Last changed · who".
   * Null when there are no entries. A list only; GET /records only.
   */
  changedBy?: RecordPerson | null;
};

/** One person, as a record row names them. `name` is null when they gave none. */
export type RecordPerson = {
  userId: string;
  name: string | null;
};

/**
 * What one entry is called, in the person's own words.
 *
 * Free text the workspace owns, so it can never be a catalogue key — the same
 * rule a choice's `label` follows. Both forms are stored, because a word a
 * person typed has no plural rule we could apply: "sale" and "sales", "dues"
 * and "dues". Each is 1 to 30 letters.
 */
export type RecordEntryNoun = {
  one: string;
  other: string;
};

/**
 * Today, across every record the caller may open.
 *
 * Summed per row, not per record, because a record whose sign is a field holds
 * rows pointing both ways. One currency: records that carry another are left
 * out, and `currency` says which one these two numbers are in.
 */
export type RecordListToday = {
  currency: string | null;
  in: number;
  out: number;
};

/**
 * GET /v1/records. Tenant header required.
 *
 * `?tz=` names the caller's IANA zone, which is how the server learns what
 * calendar day "today" is: there is no tenant timezone column yet. An absent
 * or unknown zone falls back to UTC.
 */
export type RecordListResponse = {
  today: RecordListToday;
  records: RecordListItem[];
};

/**
 * POST /v1/records. Tenant header required.
 *
 * Adds one blank record or list to a business that already exists. The
 * currency is the tenant's own, never a body field: the business answered
 * that once.
 */
export type RecordCreateRequest = { blank: RecordBlankSpec };

/**
 * What each entry of a blank record counts, in the words the screen asks it in.
 *
 * This one answer settles three bindings a person must never be asked about by
 * name: `measure` names the field the totals read, `sign` says which way the
 * money points, and `focused_input` says which box opens first. It is answered
 * once, at creation, because `PATCH /structure` holds no binding vocabulary
 * and a record that changed its mind would re-read every entry it already has.
 */
export type RecordBlankCounts = 'money_in' | 'money_out' | 'money_both' | 'count' | 'none';

/**
 * The five answers, in the order a person meets them.
 *
 * Here rather than in a route, because two routes now read it — the blank
 * create and onboarding apply — and two copies of a closed list is one copy
 * that goes stale the day a sixth answer lands.
 */
export const BLANK_COUNTS: readonly RecordBlankCounts[] = [
  'money_in',
  'money_out',
  'money_both',
  'count',
  'none',
];

export const isBlankCounts = (value: unknown): value is RecordBlankCounts =>
  typeof value === 'string' && BLANK_COUNTS.includes(value as RecordBlankCounts);

/** The body of a blank create. A directory answers `kind` and `name` only. */
export type RecordBlankSpec = {
  kind: VisibleRecordKind;
  /** The tenant's own word, trimmed. 1 to 60 letters, as a field label is. */
  name: string;
  /** Required on a record, refused on a directory: a list counts nothing. */
  counts?: RecordBlankCounts;
};

/** What the apply created, in the shape home already lists. */
export type RecordCreateResponse = { records: OnboardedRecord[] };

/**
 * POST /v1/records/:recordId/duplicate. Tenant header required.
 *
 * A new record with the same fields, choices, bindings, icon and way of
 * writing entries, and none of the entries, links or access ("Fields only").
 * `name` absent takes "<name> copy", with a number when that is taken.
 * Answered with `RecordCreateResponse`, like an add.
 */
export type RecordDuplicateRequest = { name?: string };

/**
 * GET /v1/records/:recordId/removal — what deleting one record or list would
 * take with it, for the serious-step modal — and what DELETE answers once it
 * has.
 *
 * Deleting archives the record: its entries go with it and stop being read
 * anywhere, the links that start or end at it stop, and on a list every field
 * that picks from it is taken out of its record. Entries already written in
 * those records keep what they hold.
 */
export type RecordRemovalImpact = {
  recordId: string;
  name: string;
  kind: VisibleRecordKind;
  /** Live entries that go with it. */
  entries: number;
  /** The fields in other records that pick from this list. Empty on a record. */
  pickedBy: Array<{
    recordId: string;
    recordName: string;
    fieldId: string;
    fieldLabel: string;
    /** Live entries of that record that picked something through the field. */
    entries: number;
  }>;
  /** Links that start or end here, which stop. */
  links: number;
};

/** DELETE /v1/records/:recordId */
export type RecordDeleteResponse = { removed: RecordRemovalImpact };

/**
 * GET /v1/records/:listId/references — how much each entry of a list is used.
 *
 * Counted only where this caller may read: records they may open, through
 * fields they may see, over entries within their reach (their own, for
 * someone who sees only their own).
 */
export type ListReferencesResponse = {
  /** Each live field that picks from this list, with how many entries picked. */
  usedBy: Array<{ recordId: string; fieldId: string; fieldLabel: string; entries: number }>;
  /**
   * Keyed by list entry id; an entry nobody picked is absent. `entries` is how
   * many entries picked it ("842 entries picked it"), `records` in how many
   * records ("Used in 3 records").
   */
  entries: Record<string, { entries: number; records: number }>;
};

/** How a purpose is answered: a field, or a constant when the purpose allows one. */
export type RecordBindingAnswer = { fieldId: string } | { constNum: 1 | -1 };

/**
 * The colour one choice wears when it is chosen.
 *
 * A register is read by colour before it is read by letter: on a sheet of forty
 * names, green and red say who came in faster than any word can. Four words,
 * and they are the four the design system already speaks, so nothing here has
 * to name a colour — the device maps each to its own feedback tone.
 *
 * A choice with no tone keeps the brand tint the form's chips wear today, which
 * is what every choice on every record looks like now.
 */
export type RecordCaptureTone = 'positive' | 'notice' | 'negative' | 'information';

/**
 * The four, as a list, because both sides check a stored word against it. Same
 * reason `BLANK_COUNTS` is a list: two copies of a closed set is one copy that
 * goes stale.
 */
export const CAPTURE_TONES: readonly RecordCaptureTone[] = [
  'positive',
  'notice',
  'negative',
  'information',
];

export const isCaptureTone = (value: unknown): value is RecordCaptureTone =>
  typeof value === 'string' && CAPTURE_TONES.includes(value as RecordCaptureTone);

/**
 * How a record's entries are WRITTEN, when a form one at a time is not the
 * shape of the job.
 *
 * A staff attendance record wants one screen per day: every person listed, one
 * status each, one save. So the setting lives on the RECORD and not on a field
 * — the field still says how one value looks, and this says how a day's worth
 * of them are collected. It is stored in `app.record.prefs` under `capture`.
 *
 * One mode ships, `sheet`, and a mark it writes is an ordinary entry: same
 * head, same version, same event, same totals. A later mode is this same key
 * with another word.
 *
 *   over    a reference field. One row of the list it points at, one line.
 *   cell    a select field. The mark, one tap.
 *   shared  the fields drawn once at the top rather than per line. It holds
 *           the field bound to `time_axis` — the day the sheet is for — and
 *           may add other reference fields.
 *   quick   a choice of `cell` the one-tap shortcut fills down the sheet, or
 *           null when the record offers none.
 */
export type RecordCapture = {
  mode: 'sheet';
  over: string;
  cell: string;
  shared: string[];
  quick: string | null;
};

/**
 * One choice of a select field. Not in the plan's response sketch, and added
 * on purpose: a select is undrawable without its choices, and a sign that
 * lives per option is unreadable without them. The id is what an entry stores
 * in its reference slot.
 */
export type RecordFieldOption = {
  id: string;
  /** Set on an option a template shipped. The catalogue holds the word. */
  labelKey: string;
  /**
   * Set on an option a person typed in the structure editor, and null on one a
   * template shipped. Free text, so it can never be a catalogue key: the
   * device's `t()` throws on a key it does not hold, by design, and a shop's
   * own word for a choice is not ours to translate. Read `label ?? t(labelKey)`.
   */
  label: string | null;
  sign: 1 | -1 | null;
  /**
   * The colour this choice wears when it is chosen. Null on nearly every one,
   * and then the choice keeps the brand tint the form has always drawn.
   *
   * Optional, and absent means exactly what null means. The server always
   * sends the key — `fieldOptions` in routes/records.ts fills it or nulls it —
   * so this is not a wire-level maybe; it is a courtesy to the many places
   * inside the server that build a choice list to answer one question about a
   * sign and have no colour to say anything about. Read `tone ?? null`.
   */
  tone?: RecordCaptureTone | null;
};

/**
 * Above this many options, a choice stops going on the form and goes behind a
 * bottom sheet instead.
 *
 * It lives here because it is now a contract between the two sides. The device
 * decides the shape of a field from it, and the server decides from the same
 * number whether to send a directory's rows with the schema at all (see
 * `targetRows`). Two copies of it would drift, and the drift would look like a
 * field that draws chips one moment and a sheet the next.
 *
 * Four is not new: `prefersSegmentedControl` has always drawn a native
 * segmented control at four or fewer and fallen back above it.
 */
export const INLINE_OPTION_LIMIT = 4;

/** One row of a directory a reference field points at. */
export type RecordTargetRow = {
  /** The entry's uuid. This is what a reference field reads and writes. */
  id: string;
  /** The row's `title` binding, already resolved from its slot. */
  title: string;
  /**
   * The row's own value of the field's `filter.field`: the parent row it
   * belongs to (the Category of a Sub-category). Null when the field has no
   * filter, or when the row names no parent.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  filterValue?: string | null;
};

export type RecordSchemaField = {
  id: string;
  /** Free text the tenant owns. Everything else human-facing here is a key. */
  label: string;
  type: ValueTypeId;
  tier: string;
  /** Walked through resolveComponent, so the device can always draw it. */
  component: ComponentId;
  props: Record<string, unknown>;
  required: boolean;
  ord: number;
  sticky: boolean;
  helpText: string | null;
  storage: string;
  targetRecordId: string | null;
  /** Null on every type but select. */
  options: RecordFieldOption[] | null;
  /**
   * The rows of `targetRecordId`, but only when there are few enough of them to
   * draw on the form. Null on every field that is not a reference.
   *
   * A reference field's options live in another record, so unlike a select it
   * cannot know how many there are without asking. Asking at mount would cost a
   * request per field and would fail offline, and a field that starts as a row
   * and turns into chips once the answer lands has moved the form under the
   * person's thumb. So the server answers it here.
   *
   * Three answers, and the empty one is not the same as null:
   *
   *   RecordTargetRow[]  at most `INLINE_OPTION_LIMIT` rows, all nameable.
   *                      Draw them on the form.
   *   []                 the directory holds no rows at all. The sheet opens,
   *                      and it may offer to start the list.
   *   null               too many to draw, or the target binds no title, or one
   *                      row cannot be named. The sheet opens and searches.
   *
   * The empty case used to be folded into null, which left the picker unable to
   * tell "nothing to show you" from "too much to show you" — and so unable to
   * offer the one thing a person on their first day needs.
   *
   * This is also how a five-row Payment mode directory stops costing a
   * full-screen sheet, a keyboard and two taps.
   */
  targetRows: RecordTargetRow[] | null;
  /**
   * The name of `targetRecordId`, so the field can say which list it opens.
   *
   * The field is handed an id and has no way to look one up. `target_record` in
   * `props` is a record id and not a word, so it cannot be shown to anybody.
   * Null on every field that is not a reference, and on one with no target.
   */
  targetName: string | null;
  /**
   * What a new row in `targetRecordId` must be given, so the picker knows
   * whether adding one can be a single tap or has to be a form.
   *
   *   'title'     the title binding is the only live field. One tap is the
   *               whole row, and there is nothing else to offer.
   *   'optional'  there are more fields, none of them required. One tap, and a
   *               way through to the rest for whoever wants it.
   *   'required'  a live field other than the title is required, or the target
   *               binds no title at all. A one-tap write would be refused on
   *               the next push, silently and long after the sheet closed, so
   *               the form is the only honest answer.
   *
   * Null on every field that is not a reference, and on one with no target.
   */
  /**
   * The recipe of a computed field, from `app.field.spec`. Null on every other
   * type.
   *
   * The device needs it, not just the server: Section 5.7 rule 6 says the
   * device runs the same rule, so the phone must hold the tree to work the
   * value out offline and to draw a live preview while the person types.
   */
  spec: FieldSpec | null;
  /**
   * What this field STARTS with on a new entry, from `app.field.default_expr`.
   * Null on a field that starts with nothing, which is nearly all of them.
   *
   * The same tree a worked-out field keeps, and the difference is who owns the
   * answer: a formula owns its value forever, and a default hands one over. It
   * fills when the form opens and again when a field it reads changes, until
   * the person types into the field — from then on the value is theirs.
   *
   * The form owns the fill, so the device needs the tree: the server validates
   * and stores it and never applies it on write.
   */
  startsWith: FormulaSpec | null;
  /**
   * When this field shows on the form, from `app.field.default_expr.showWhen`
   * (Decision 11 of the lines plan): a yes-or-no over the typed fields of the
   * row and one hop. Null on a field that always shows. A hidden field saves
   * no value and is never required; `isShown` in formula.ts is the rule, and
   * the server runs it too.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  showWhen?: FormulaSpec | null;
  targetCreate: 'title' | 'optional' | 'required' | null;
  /**
   * The filter of a dependent choice, from `app.field.spec`: offer only the
   * rows of `targetRecordId` whose `field` equals this entry's `equals`. Null
   * on a field with no filter, and on every type but reference and
   * multi_select. `rowMatches` in formula.ts is the rule.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  filter?: ChoiceFilter | null;
  /**
   * The most rows a multi_select may hold, from `app.field.spec`. Null when
   * there is no limit, and on every other type. Optional on the wire for the
   * same reason as `filter`.
   */
  maxChosen?: number | null;
  /**
   * A `lines` field's lines record, the count field that starts its lines,
   * and the most it holds, from `app.field.spec` (Decision 4 of
   * docs/plans/entry-lines-and-layout.md). Null on every other type. The
   * lines record's own schema is in the response's `lineSchemas`, under this
   * field's id.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? null`.
   */
  lines?: LinesFieldSpec | null;
};

/** GET /v1/records/:recordId/schema?registry_version=NN */
export type RecordSchemaResponse = {
  recordId: string;
  name: string;
  fieldSetVer: number;
  nounKey: string;
  entryNounKey: string;
  currency: string | null;
  bindings: Partial<Record<PurposeId, RecordBindingAnswer>>;
  fields: RecordSchemaField[];
  /**
   * How this record's entries are written, or null for the ordinary form.
   *
   * Null is also the answer when the caller cannot see one of the fields the
   * setting names: a sheet that cannot draw its own list or its own mark is
   * not a sheet, and the honest fallback is the form, which this same payload
   * already describes in full. Section 11's masking rule, applied to a
   * setting rather than to a field.
   *
   * Optional, and absent means exactly what null means. `loadRecordSchema` is
   * the one assembly of this payload and it always answers the key, so nothing
   * on the wire is ever without it; the option is for the fixtures and stubs
   * that stand a schema up to test something else entirely, none of which has
   * an opinion about how entries are captured. Read `capture ?? null`.
   */
  capture?: RecordCapture | null;
  /**
   * Where each field of the form shows: sections, and the fields in each
   * (Decision 12 of docs/plans/entry-lines-and-layout.md). The server has
   * already taken out the node of every field this payload does not carry.
   *
   * Null when the record has no tree, when the stored tree does not read, or
   * when its version is newer than the server knows: the form is then drawn
   * in field order, as it always was. A device checks the version again
   * (`formPlan` does), because an old build can hold a newer schema in its
   * cache. Optional on the same terms as `capture`: read `layout ?? null`.
   */
  layout?: RecordLayout | null;
  /**
   * What this record holds, in one word, and the EFFECTIVE answer as the
   * catalog's own `icon` is: the stored name, or the default for a record that
   * was never given one. Decision 3 of docs/plans/done/record-icons-and-list-tiles.md.
   *
   * The schema carries it as well as the catalog because a record is opened
   * from more than the home list — a link, a search, the assistant — and a
   * screen that has the schema should never have to ask the catalog what glyph
   * to draw.
   *
   * Optional on the same terms `capture` is: `loadRecordSchema` always answers
   * the key, and the option is for the fixtures that stand a schema up to test
   * something else. Read `icon ?? null`.
   */
  icon?: RecordIconId | null;
  /** The person's own word for one entry, or null. Read `entryNoun ?? null`. */
  entryNoun?: RecordEntryNoun | null;
  views: Array<{ id: string; nameKey: string; type: string; spec: object; ord: number }>;
  /**
   * The schema of each lines record this record's `lines` fields hold, keyed
   * by the `lines` field's id, so a form draws its lines offline from the one
   * schema it already holds. A depth-1 schema holds its own `lineSchemas`.
   * A line schema leaves out the parent and root fields (the server sets
   * them; their bindings stay, so a line formula's hop through them is
   * answered from the form above) and every field this caller's access
   * masks. A lines record has no field_set_ver a device tracks on its own:
   * a change to it moves the field_set_ver of the records above it, so this
   * map is refreshed with them.
   *
   * Optional on the wire, so a schema cached before it existed still reads:
   * read it with `?? {}`.
   */
  lineSchemas?: Record<string, RecordSchemaResponse>;
};

/* -------------------------------------------------------- structure editing */

/**
 * The customize-entry-flow API: one read, one write.
 *
 * This is the write side of Section 3 of docs/records-platform-architecture.md,
 * "what fully customizable means". Every knob below is already a column on
 * app.field; none of them had a screen until now.
 *
 * Two rules shape the shapes.
 *
 * 1. The editor reads more than the form does. GET /schema answers the live
 *    field set, so a switched-off field is absent from it. The editor must see
 *    that field to switch it back on, so it gets its own read.
 * 2. One save, one transaction. The screen collects a draft and saves once, so
 *    the write takes the whole change set: adds, patches, archives, the order,
 *    and the focused field. A half-applied structure is not a state the app
 *    should ever be able to reach.
 */

/** One field as the editor reads it. Every knob, switched on or off. */
export type StructureField = {
  id: string;
  label: string;
  type: ValueTypeId;
  /** primary is the template's and cannot be switched off. custom is the user's. */
  tier: 'primary' | 'secondary' | 'custom';
  enabled: boolean;
  required: boolean;
  sticky: boolean;
  ord: number;
  helpText: string | null;
  /** As stored, never resolved: the editor must show what was asked for. */
  component: ComponentId;
  props: Record<string, unknown>;
  storage: string;
  targetRecordId: string | null;
  /** Null on every type but select. */
  options: RecordFieldOption[] | null;
  unitList: string[] | null;
  /**
   * The recipe of a computed field, from `app.field.spec`. Null on every other
   * type.
   *
   * The device needs it, not just the server: Section 5.7 rule 6 says the
   * device runs the same rule, so the phone must hold the tree to work the
   * value out offline and to draw a live preview while the person types.
   */
  spec: FieldSpec | null;
  /** What the field starts with on a new entry, from `default_expr.formula`. */
  startsWith: FormulaSpec | null;
  /** When the field shows on the form, from `default_expr.showWhen`. Null: always. */
  showWhen?: FormulaSpec | null;
  /**
   * The purposes that name this field. A field the engine reads cannot be
   * switched off or archived, and this is what lets the screen say so before
   * the server refuses.
   */
  boundPurposes: PurposeId[];
};

/**
 * How many typed slots this record has left, per family.
 *
 * A record holds six numeric slots, six text, four time, six reference, and
 * two boolean. Past that a new field lands in the attrs document, which cannot
 * hold `not null`, so it cannot be required. The screen needs both facts to
 * say something true before a person types a name.
 */
export type StructureCapacity = {
  number: number;
  text: number;
  date: number;
  reference: number;
  boolean: number;
};

/** GET /v1/records/:recordId/structure */
export type RecordStructureResponse = {
  recordId: string;
  name: string;
  nounKey: string;
  entryNounKey: string;
  currency: string | null;
  /** Bumped by every structure write. A draft carries it back to prove it is fresh. */
  fieldSetVer: number;
  /** False for a reader: the screen draws itself, and every control is quiet. */
  mayEdit: boolean;
  fields: StructureField[];
  bindings: Partial<Record<PurposeId, RecordBindingAnswer>>;
  capacity: StructureCapacity;
  /**
   * How this record's entries are written, straight off `app.record.prefs`.
   *
   * Unmasked, unlike the schema's copy: the editor reads the record whole, the
   * way it reads a switched-off field, because it is the screen that has to be
   * able to change the setting.
   */
  capture: RecordCapture | null;
  /**
   * Where each field shows, straight off `app.record.prefs.layout`, read with
   * every live field and unmasked, as `capture` is. Null when there is no tree
   * or it does not read. Optional so a fixture need not stand one up.
   */
  layout?: RecordLayout | null;
  /**
   * How deep this record sits in a tree of lines: 0 on a record or a list, 1
   * on a lines record whose parent is a record, 2 below that. The editor
   * offers a new `lines` field only while one more level fits
   * (`LINES_MAX_DEPTH`). Optional so a fixture need not stand one up; absent
   * reads as 0.
   */
  linesDepth?: number;
  /**
   * The icon this record was GIVEN, straight off `app.record.prefs`, and null
   * when it was never given one.
   *
   * The stored name, unlike the schema's and the catalog's copies, which both
   * answer the effective one. The editor is the screen that CHANGES the
   * setting, so it has to be able to say "Default" — and a default it was
   * handed as a name would be saved back as a choice the person never made.
   */
  icon: RecordIconId | null;
  /** The directories a reference field may point at, with their own words. */
  directories: Array<{ id: string; name: string }>;
  /**
   * The person's own word for one entry, or null while the record says
   * `entryNounKey`. The server always answers it; optional for the fixtures.
   */
  entryNoun?: RecordEntryNoun | null;
  /**
   * How the totals work today, read off the `measure` and `sign` bindings.
   * The server always answers it; optional for the fixtures. Absent on a list.
   */
  totals?: RecordTotals;
};

/**
 * How a record's totals work, as Customize asks it: "How totals work".
 *
 *   money_in    the measure (a money field) adds to money in, every entry
 *   money_out   the same, to money out
 *   money_both  the measure, and a choice field whose choices each say in or
 *               out, so every entry says which
 *   count       a number or quantity field, counted and not money
 *   none        no amount and no total
 *
 * The same five answers `RecordBlankCounts` gives at creation. `measure` and
 * `sign` are field ids, or add keys of the same structure save, as
 * `focusedInput` is.
 *
 * Changing it moves every past entry with it: the totals are worked out again
 * from the entries in the same save. No entry is rewritten.
 */
export type RecordTotals =
  | { counts: 'money_in' | 'money_out' | 'count'; measure: string }
  | { counts: 'money_both'; measure: string; sign: string }
  | { counts: 'none' };

/** One option as the editor sends it. No id means a new choice. */
export type StructureOptionInput = {
  id?: string;
  label: string;
  sign?: 1 | -1 | null;
  /**
   * The colour the choice wears once it is chosen. `null` takes a tone off and
   * an absent key leaves whatever the choice already wears, the same rule
   * `startsWith` follows a few types down.
   */
  tone?: RecordCaptureTone | null;
};

/** A change to a field that already exists. Absent means leave it alone. */
export type StructureFieldPatch = {
  id: string;
  label?: string;
  helpText?: string | null;
  required?: boolean;
  enabled?: boolean;
  sticky?: boolean;
  component?: ComponentId;
  props?: Record<string, unknown>;
  options?: StructureOptionInput[];
  unitList?: string[] | null;
  /**
   * A new recipe for a computed field. It applies to entries saved from now on
   * and never backfills, which is the standing rule for a field change.
   */
  spec?: FieldSpec | null;
  /**
   * What the field starts with from now on. `null` clears it, which is the
   * whole reason this one is nullable: a person taking a default off has to be
   * able to say so, and an absent key already means "leave it alone".
   *
   * Entries already saved are untouched, like every other field change.
   */
  startsWith?: FormulaSpec | null;
  /**
   * When the field shows from now on: a yes-or-no rule. `null` takes it off,
   * so the field always shows, for the same reason `startsWith` is nullable.
   */
  showWhen?: FormulaSpec | null;
};

/** A field that does not exist yet. */
export type StructureFieldAdd = {
  /** The client's handle for it, so `order` and `focusedInput` can name it. */
  key: string;
  label: string;
  type: ValueTypeId;
  /** Absent takes the value type's primitive, which every device can draw. */
  component?: ComponentId;
  props?: Record<string, unknown>;
  required?: boolean;
  sticky?: boolean;
  helpText?: string | null;
  targetRecordId?: string | null;
  options?: StructureOptionInput[];
  unitList?: string[] | null;
  /** Required on a `formula`, refused on every other type. */
  spec?: FieldSpec | null;
  /**
   * What the new field starts with on a new entry. Absent and null both mean
   * nothing, and the two are the same thing on a field that does not exist yet.
   */
  startsWith?: FormulaSpec | null;
  /** When the new field shows on the form. Absent and null both mean always. */
  showWhen?: FormulaSpec | null;
  /**
   * Required on a `lines` field, refused on every other type: the fields each
   * line holds. The server makes the lines record in the same save, with its
   * parent and root fields, so a lines field never names a record of its own.
   */
  lines?: StructureLinesAdd;
};

/**
 * The lines record a new `lines` field makes.
 *
 *   fields     what each line holds, at most `LINES_MAX_FIELDS`. A field of
 *              type `lines` among them makes a second level, and the planner
 *              refuses a third (`LINES_MAX_DEPTH`).
 *   countFrom  a `number` field on the same row, by id or by add key, whose
 *              value starts that many lines. Absent or null: none does.
 *   max        the most lines one row holds, 1 to `ENTRY_BATCH_MAX`. Absent
 *              takes `ENTRY_BATCH_MAX`.
 */
export type StructureLinesAdd = {
  fields: StructureFieldAdd[];
  countFrom?: string | null;
  max?: number;
};

/**
 * What a structure save answers with: the editor's truth, and the form's.
 *
 * The schema is here so a device does not have to wait for the next sync pull
 * to draw the form it has just changed. It is exactly what `GET /schema` would
 * answer this client, built in the same transaction as the write, so the
 * mirror can take it as it stands.
 *
 * A separate type rather than an optional field on `RecordStructureResponse`:
 * that type is also the GET answer and the draft's own `base`, and a schema
 * riding along inside a draft would sit there going stale.
 */
export type RecordStructureSaveResponse = {
  structure: RecordStructureResponse;
  schema: RecordSchemaResponse;
};

/** PATCH /v1/records/:recordId/structure */
export type RecordStructureRequest = {
  /** The version the draft was built on. A mismatch is refused, never merged. */
  fieldSetVer: number;
  add?: StructureFieldAdd[];
  update?: StructureFieldPatch[];
  archive?: string[];
  /** Every field of the record after the change, in form order. */
  order?: string[];
  /** A field id, an add key, or null to clear. Absent leaves it alone. */
  focusedInput?: string | null;
  /**
   * How this record's entries are written from now on.
   *
   * Every field it names is a field id, or an add key of this same request —
   * a person turning a record into a sheet in one save names the fields they
   * are adding beside it, exactly as `focusedInput` and `order` do. `null`
   * takes the setting off and an absent key leaves it alone, so a save that
   * only renames a field can never drop a sheet by not mentioning it.
   */
  capture?: RecordCapture | null;
  /**
   * Where each field of the form shows from now on (Decision 12 of
   * docs/plans/entry-lines-and-layout.md). A field node names a field id or
   * an add key of this same request, as `order` does. `null` takes the tree
   * off and the form goes back to field order; an absent key leaves it alone.
   * A tree that does not read is refused with the `LAYOUT_*` code of
   * `judgeLayout`, never stored.
   */
  layout?: RecordLayout | null;
  /**
   * What this record holds from now on, one name of `RECORD_ICON_IDS`.
   *
   * `null` puts the record back to its default glyph and an absent key leaves
   * whatever it wears alone, the same rule `capture` and `startsWith` follow: a
   * save that only renames a field can never take an icon off by not
   * mentioning it. A name outside the list is refused (`ICON_UNKNOWN`), never
   * stored and never guessed at.
   */
  icon?: RecordIconId | null;
  /** app.record.name. */
  name?: string;
  /**
   * What one entry is called from now on. `null` puts the record back to its
   * `entryNounKey`; an absent key leaves it alone. Refused on nothing but its
   * length: 1 to 30 letters for each form.
   */
  entryNoun?: RecordEntryNoun | null;
  /** How the totals work from now on. Absent leaves them alone. Refused on a list. */
  totals?: RecordTotals;
};

/** One refusal. `fieldId` carries an add key when the problem is about a new field. */
export type StructureProblem = {
  code: string;
  fieldId: string | null;
  message: string;
};

/** The 400 body a refused structure write answers with. Every problem, not the first. */
export type RecordStructureProblemsResponse = {
  error: { code: 'STRUCTURE_INVALID'; message: string; problems: StructureProblem[] };
};

/* ------------------------------------------------------------------ entries */

/**
 * One value as the entry API carries it, keyed by app.field.id.
 *
 * Structurally identical to the server's internal EntryValue in
 * apps/server/src/entry-values.ts, and declared again here on purpose: that
 * module is the storage router and owns the slot vocabulary, this one is the
 * transport contract the device compiles against. Neither imports the other.
 */
export type EntryValue = string | number | boolean | readonly string[] | null;

export type EntryValues = Readonly<Record<string, EntryValue>>;

/**
 * POST /v1/records/:recordId/entries, and the body of PATCH .../:entryId.
 * entryId/clientOpId are present on offline-first creates: the device names
 * the entry while offline and pushes later, so a retried push is a replay,
 * not a second entry.
 */
export type EntryWriteRequest = {
  values: EntryValues;
  /** Device-generated logical entry id. When present, a unique conflict is a replay, not an error. */
  entryId?: string;
  /** Device-generated op id, for replay detection and support logs. */
  clientOpId?: string;
  /**
   * The lines of this entry, keyed by the id of the `lines` field that holds
   * them. Written in the same transaction as the entry. An edit sends the full
   * set for each field it names: a line left out is removed with its own
   * lines, and a field it does not name keeps its lines.
   */
  lines?: EntryLinesWrite;
  /**
   * An edit only: the version of the entry the edit was read at. When the
   * entry has moved since, the edit is refused (409 ENTRY_MOVED), so a set of
   * lines read earlier never removes a line someone added after it.
   */
  baseVersion?: number;
};

/**
 * How deep lines go. A depth-1 line belongs to an entry; a depth-2 line
 * belongs to a depth-1 line (a guest of a room of a stay). The planner refuses
 * a third level.
 */
export const LINES_MAX_DEPTH = 2;

/**
 * The most lines one save holds, across both levels. Without it, fifty rooms
 * of fifty guests would be 2,500 entries in one transaction. A batch holds at
 * most this many across all of its entries.
 */
export const LINES_MAX_PER_ENTRY = 200;

/**
 * The most fields one lines record holds, beside the parent and root fields
 * the server makes. A line is a short row, not a second form.
 */
export const LINES_MAX_FIELDS = 8;

/**
 * One line as a write carries it. The device names `entryId`, as it names an
 * entry it saves offline, so a retried save is a replay. `lines` is present
 * only on a depth-1 line whose record holds lines of its own.
 */
export type LineWrite = {
  readonly entryId: string;
  readonly values: EntryValues;
  readonly lines?: EntryLinesWrite;
};

/** The lines of one row, keyed by the id of the `lines` field that holds them. */
export type EntryLinesWrite = Readonly<Record<string, readonly LineWrite[]>>;

/** Nothing in a value a line sends: none, a blank string, an empty list. A zero and a false are values. */
const isBlankLineValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/**
 * The lines of a write without the ones that hold nothing: every value the
 * line sends is blank, and no line under it is left once its own empty lines
 * are left out. Decided 5 Oct 2026: such a line is not saved, on the phone and
 * on the web alike, so both ask this one function.
 *
 * A write carries typed values only, so a line holding nothing but a
 * worked-out answer is empty here, and one holding a filled default is not.
 * On a create an empty line is never made. On an edit, a saved line the person
 * emptied is left out of its run, and a live line a named run leaves out is
 * one the server tombstones. So every run keeps its key, with whatever is left
 * in it, an empty run included.
 */
export const withoutEmptyLines = (lines: EntryLinesWrite): EntryLinesWrite => {
  const out: Record<string, readonly LineWrite[]> = {};
  for (const [linesFieldId, run] of Object.entries(lines)) {
    out[linesFieldId] = run.flatMap((line): LineWrite[] => {
      const under = line.lines === undefined ? undefined : withoutEmptyLines(line.lines);
      const holdsLines = under !== undefined && Object.values(under).some((one) => one.length > 0);
      if (!holdsLines && Object.values(line.values).every(isBlankLineValue)) return [];
      return [under === undefined ? line : { ...line, lines: under }];
    });
  }
  return out;
};

/**
 * The noun key a lines record's lines are called by, from its own
 * `entryNounKey`. A lines record is made with its field and carries the
 * default entry noun, and a line is not an entry (Decision 19 of
 * docs/plans/entry-lines-and-layout.md): with no key of its own a line is a
 * "line", on the phone and on the web alike. Words an owner set
 * (`entryNoun`) come before either.
 */
export const lineNounKeyOf = (entryNounKey: string | undefined): string =>
  entryNounKey === undefined || entryNounKey === 'noun.entry' ? 'noun.line' : entryNounKey;

/**
 * One line as a read carries it: the same tree a write sends, so a form
 * reads what it saved. A depth-2 line has no `lines`.
 */
export type LineRead = {
  readonly entryId: string;
  readonly values: EntryValues;
  readonly lines?: EntryLinesRead;
};

/** The lines of one row on a read, keyed by `lines` field id. */
export type EntryLinesRead = Readonly<Record<string, readonly LineRead[]>>;

export type EntryWriteResponse = {
  entryId: string;
  versionId: string;
  version: number;
  serverSeq: number;
  /**
   * What this save's links did, when a link's rule held: the rows they posted
   * and the ones they could not write. Absent when no rule held, and on a
   * replay (the first answer carried it). A rule that did not hold is said
   * nowhere (L3 of docs/plans/links-fixes.md). Not `links`: that word is the
   * reference rows an entry points at.
   */
  linkFirings?: EntryLinkOutcome;
};

/** One row a link of the saved entry wrote, changed or took out. */
export type EntryLinkPosted = {
  linkId: string;
  toRecordId: string;
  /** Null when the person saving may not open the record it went into. */
  toRecordName: string | null;
  did: 'added' | 'changed' | 'removed';
};

/**
 * One link whose rule held but which could not write its row. The save still
 * stands; this is said every time, with why and what to do.
 */
export type EntryLinkRefused = {
  linkId: string;
  /** The record the link belongs to, whose Links page changes it. */
  fromRecordId: string;
  /** Its name, so the words can say where the link is changed. */
  fromRecordName: string | null;
  toRecordId: string;
  /** Null when the person saving may not open the record it was to go into. */
  toRecordName: string | null;
  /** The firing's code: `TARGET_UNKNOWN`, `TARGET_MODE_UNSUPPORTED`, `MAP_INVALID:<problem>`. */
  code: string;
  /** Why, and what to change, in the server's words. */
  reason: string;
  /** Whether the person saving may change links, so the words offer the fix or say whom to ask. */
  mayFix: boolean;
};

export type EntryLinkOutcome = {
  posted: EntryLinkPosted[];
  refused: EntryLinkRefused[];
};

/**
 * GET /v1/records/:recordId/entries/:entryId/links — what the entry's links did
 * the last time it was saved: the rows they hold now, and the links that held
 * but could not write. The entry page says both.
 */
export type EntryLinksResponse = EntryLinkOutcome;

/** DELETE /v1/records/:recordId/entries/:entryId */
export type EntryDeleteResponse = { entryId: string; version: number };

/** The most entries one remove takes: the largest page the table shows. */
export const ENTRY_REMOVE_MAX = 500;

/**
 * POST /v1/records/:recordId/entries/delete — several entries at once, the
 * bulk bar's Remove.
 *
 * All or nothing, in one transaction. Every entry is checked first — it is
 * live, a link did not write it, and it is within the caller's reach of
 * Remove (their own entries only, for someone who sees only their own) — and
 * one refusal refuses the lot, with every problem named
 * (`EntryRemoveProblemsResponse`). A repeated id counts once.
 */
export type EntryRemoveRequest = { entryIds: string[] };

export type EntryRemoveResponse = { removed: EntryDeleteResponse[] };

export type EntryRemoveProblem = {
  entryId: string;
  code: 'ENTRY_UNKNOWN' | 'ENTRY_IS_DERIVED' | 'NOT_YOURS' | 'DIRECTION_NOT_ALLOWED' | 'ENTRY_UNREADABLE';
  message: string;
};

/** The 409 body a refused remove answers with. */
export type EntryRemoveProblemsResponse = {
  error: { code: 'ENTRIES_REFUSED'; message: string; problems: EntryRemoveProblem[] };
};

/**
 * GET /v1/records/:recordId/export?format=csv&columns=a,b&filter=<json>, or
 * POST to the same path with an `EntryExportRequest` body when the filter is
 * too long for an address (a few hundred selected entries).
 *
 * The answer is the file itself, streamed: every entry this caller may read
 * that the filter keeps, newest first, read from the server and never from
 * what a device has loaded. A field this caller may not see is left out of
 * the file whether or not `columns` names it; `columns` absent means every
 * field they may see, in form order. A filter that names a field they may not
 * see is refused, never quietly dropped.
 */
export const ENTRY_EXPORT_FORMATS = ['csv'] as const;
export type EntryExportFormat = (typeof ENTRY_EXPORT_FORMATS)[number];

/**
 * One condition of an export filter. The same three shapes as the device's
 * list filter (`EntryFieldFilter` in apps/mobile/src/lib/entry-filter.ts), so
 * the table's filter travels as it stands: OR within one field, AND across
 * fields, and `none_of` keeps an entry that left the field empty.
 */
export type EntryExportCondition =
  | { fieldId: string; op: 'any_of' | 'none_of' | 'all_of'; values: (string | number)[] }
  | { fieldId: string; op: 'gte' | 'lte'; value: number }
  | { fieldId: string; op: 'set' | 'unset' };

export type EntryExportFilter = {
  /**
   * Calendar days on the record's time axis, `YYYY-MM-DD`, both inclusive.
   * The device resolves a preset ("this month") against its own today.
   */
  from?: string;
  to?: string;
  fields?: EntryExportCondition[];
  /** Words, as the table's search reads them. Matched against what the caller may see. */
  q?: string;
  /** Exactly these entries: "the selected ones". At most `ENTRY_REMOVE_MAX`. */
  entryIds?: string[];
};

export type EntryExportRequest = {
  format: EntryExportFormat;
  columns?: string[];
  filter?: EntryExportFilter;
};

export type EntryListItem = {
  entryId: string;
  version: number;
  values: EntryValues;
  /** data.entry_head.created_at. When the logical entry was born. */
  createdAt: string;
  /** The live version's created_at. Moves on every edit. */
  updatedAt: string;
  createdBy: string;
  sourceChannel: string;
  /**
   * The Link that wrote this row, and the entry it was written from. Null on a
   * row a person typed. Section 5.7 rule 2: the pair draws the Auto badge and
   * blocks a direct edit, so both travel with every row.
   */
  linkId: string | null;
  sourceEntryId: string | null;
  /**
   * The lines of this entry, keyed by `lines` field id, with depth-2 lines
   * under their depth-1 line. Absent on a record that holds no `lines` field.
   */
  lines?: EntryLinesRead;
};

/**
 * GET /v1/records/:recordId/entries?limit=&cursor=&q=
 *
 * `references` is why the device can draw a row at all: the title binding of a
 * sales record is a reference field, so `values` carries a uuid and nothing
 * else. The server resolves each target's own title binding, because D6 says
 * the server sends meaning.
 */
/**
 * POST /records/:recordId/fields/:fieldId/to-list. The new list, and how many
 * entries moved their choice to its rows. A refusal comes back as ApiError.
 */
export type FieldToListResponse = {
  readonly list: { readonly id: string; readonly name: string };
  readonly fieldId: string;
  readonly converted: number;
};

export type EntryListResponse = {
  entries: EntryListItem[];
  /** Title of every row this page's reference slots point at, keyed by entry id. */
  references: Record<string, string>;
  /** base64url of { d, s } over (the time axis desc nulls last, server_seq desc). */
  cursor: string | null;
};

/** One field-level refusal, mirrored from the server's EntryValueProblem. */
export type EntryProblem = {
  code: string;
  /** Null when the problem is not about one field. */
  fieldId: string | null;
  message: string;
  /**
   * The line the problem is on, when it is on one of the entry's lines at
   * either level, never the entry itself. The form opens that line and marks
   * `fieldId` in it.
   */
  lineEntryId?: string;
};

/** The 400 body a refused write answers with. Every problem, not the first. */
export type EntryProblemsResponse = {
  error: { code: 'ENTRY_INVALID'; message: string; problems: EntryProblem[] };
};

/**
 * The most entries one batch may carry. Fifty is a sheet of a real crew, not a
 * round number: an attendance sheet writes one mark per person per day, and a
 * shop with more than fifty on it pushes two batches rather than one long
 * transaction holding a record's aggregate row.
 */
export const ENTRY_BATCH_MAX = 50;

/**
 * POST /v1/records/:recordId/entries/batch — 1 to ENTRY_BATCH_MAX creates in
 * one transaction, each checked exactly as a single POST is.
 *
 * `entryId` is REQUIRED here, unlike the single write: a batch is only ever
 * pushed by a device draining its own outbox, every op of which already named
 * its entry, and that name is what makes a retried batch a replay rather than
 * a second sheet of marks.
 */
export type EntryBatchItem = {
  entryId: string;
  /** Device-generated op id, for support logs. Validated and carried no further. */
  clientOpId?: string;
  values: EntryValues;
  /** The entry's lines, as on a single write. LINES_MAX_PER_ENTRY caps the batch as a whole. */
  lines?: EntryLinesWrite;
};

export type EntryBatchRequest = { entries: EntryBatchItem[] };

/** 201: one receipt per request entry, in request order. */
export type EntryBatchResponse = { entries: EntryWriteResponse[] };

/**
 * One refusal of a batch, and which entry of the REQUEST it is about.
 *
 * The index rides beside `fieldId` rather than inside it (`0:f_amount`) so the
 * phone reads a number where it expects a number and a field id where it
 * expects a field id: every existing reader of `EntryProblem` keeps working on
 * one of these, and the one new reader — the push, deciding which op to drop —
 * reads `entryIndex` without parsing anything.
 */
export type EntryBatchProblem = EntryProblem & { entryIndex: number };

/**
 * The 400 body a refused batch answers with. A batch is all or nothing, so one
 * bad entry rolls back every entry beside it.
 *
 * `code` is the same vocabulary a single write answers with — `ENTRY_INVALID`,
 * `DIRECTION_NOT_ALLOWED`, `ENTRY_ID_CONFLICT` — but the status is always 400,
 * where the single route would answer 403 or 409. The batch is an
 * optimisation and its only job on a refusal is to tell the phone to stop
 * batching: the push then replays those ops one at a time, and the single
 * route answers each one its true status.
 */
export type EntryBatchProblemsResponse = {
  error: { code: string; message: string; problems: EntryBatchProblem[] };
};

/* -------------------------------------------------------------------- sync */

/**
 * The offline-first sync contract. The server is the authority; the device
 * mirrors data and reconciles by cursor. Three pieces: a pull that carries
 * every entry version past the cursor, a websocket that only ever announces
 * that the cursor moved, and the idempotent create above that makes a
 * retried push safe. server_seq is dense per tenant, assigned by trigger, so
 * it is the one ordering both sides trust.
 */

/** POST /v1/sync/pull body. */
export type SyncPullRequest = {
  /** Device cursor: server_seq it has applied up to. 0 means full snapshot. */
  since: number;
  /** recordId -> field_set_ver the device holds; schemas that differ are resent in full. */
  schemaVersions: Record<string, number>;
  /**
   * The registry the device can draw, for the same component negotiation the
   * schema route performs. Absent means the floor, as there.
   */
  registryVersion?: number;
  /**
   * Ask for `remaining` in the answer. The device sends it on the first page
   * of a run only, because the count is a second read over the whole stream.
   */
  withRemaining?: boolean;
  /**
   * The oldest day the device wants rows for, `yyyy-mm-dd`. Honoured ONLY on a
   * full fill (`since` 0), and ignored on every later page.
   *
   * That restriction is the design, not a shortcut. The snapshot is the large
   * answer and the window pays there. The incremental stream is small, so every
   * version past the cursor travels whatever its day, and the device's own
   * prune decides what to keep. A day filter on the incremental stream would
   * silently strand a row the device already holds: an entry inside the window,
   * edited to a date outside it, whose new version would never arrive.
   *
   * A row with no day — a record with no time axis, so a directory — is never
   * filtered by this. The picker reads those lists, and they must arrive whole.
   */
  fromDay?: string;
  /**
   * True on every page of a run that began at cursor 0.
   *
   * `fromDay` is honoured only under this flag. `since === 0` cannot stand in
   * for it: a full fill is a hundred pages and only the first carries a zero
   * cursor, so a window keyed on the cursor would filter one page and let the
   * other ninety-nine through — on exactly the download it exists to shrink.
   */
  fullFill?: boolean;
};

/**
 * The response header that carries the size of the pull body as it was sent.
 *
 * A header and not `content-length`, because Android's HTTP client removes
 * `content-length` when it unzips a gzip body. The device adds this number up
 * to show how much of a first download has arrived.
 */
export const SYNC_BYTES_HEADER = 'x-alluzo-bytes' as const;

/** One entry version as the pull carries it: field-id keyed values, never slot columns. */
export type SyncEntryRow = {
  entryId: string;
  recordId: string;
  versionId: string;
  version: number;
  serverSeq: number;
  isCurrent: boolean;
  /** True on a tombstone version. values is empty on a tombstone. */
  deleted: boolean;
  values: EntryValues;
  createdBy: string;
  createdAt: string;
  sourceChannel: string;
  /** Set together on a derived row. Null on one a person typed. */
  linkId: string | null;
  sourceEntryId: string | null;
};

/**
 * GET /v1/search?q=&limit=&cursor=
 *
 * Every record of one business, searched on the server, so a person finds an
 * entry from three years ago that this phone stopped holding months back.
 *
 * It answers the same three questions the phone answers, in the same order:
 * the records whose own NAME matched, the entries whose own WORDS matched, and
 * the entries POINTING AT a row whose name matched. The third is why a wage row
 * is found by typing a worker's name — the row holds his id, and his own row
 * holds the name.
 *
 * NO WINDOW. It reads all of time. That was measured before it was decided: a
 * trigram index finds rows by their words rather than walking back through
 * days, so three years costs the same as one.
 */
export type SearchResponse = {
  /** Records whose own name matched, in catalog order. */
  records: { recordId: string; name: string }[];
  /** Entries that matched, newest first. `recordId` says which record to draw them as. */
  hits: SearchHitItem[];
  /** Titles for every reference slot the hits point at, keyed by entry id. */
  references: Record<string, string>;
  /** Where the next page starts, or null when this page was the last. */
  cursor: string | null;
};

/** One entry that matched, with the record it belongs to. */
export type SearchHitItem = EntryListItem & { recordId: string };

/** The record_headline aggregate for one record, for client reconciliation. */
export type SyncAggregateRow = {
  recordId: string;
  measureFieldId: string;
  value: number;
  asOfSeq: number;
};

/**
 * One DAY of one record, pre-summed: four numbers and nothing else.
 *
 * The `record_day` scope of Section 5.10, as one row on the wire. It exists so
 * a card whose window reaches past what the device still holds is answered from
 * a sum rather than from rows that are no longer there. A device that prunes
 * old entries keeps every bucket, because three years is about eleven hundred
 * days and three years of entries is half a million rows.
 *
 * **A day and not a month, and that is load bearing.** A card's window is built
 * out of a scope, and only `month` and `year` land on month boundaries; `day`,
 * `week` and every range a person drags out of the date picker do not. A month
 * bucket asked for 27 June to 3 July could only answer by counting days nobody
 * asked about. A day bucket answers every window exactly, and a series keyed by
 * day is the shape the app already draws.
 *
 * **Four numbers, because a net cannot be taken apart again.** A day with a 500
 * in and a 500 out nets to nought, and "money in today" is not nought. So the
 * two sides are counted as they are written, beside the net and the row count.
 *
 * `day` is `yyyy-mm-dd`, cut from the entry's OWN time axis and never from the
 * day it was written. A backdated entry belongs to the day it names.
 */
export type SyncBucketRow = {
  recordId: string;
  /** The record's measure field id, or null when it binds none and only the count travels. */
  measureFieldId: string | null;
  /** `yyyy-mm-dd`. */
  day: string;
  /** The measure, summed and signed. Nought when the measure is masked or absent. */
  value: number;
  /** How many entries that day. Never masked: the catalog already carries a record's count. */
  rows: number;
  /** The positive side of the measure, summed. */
  moneyIn: number;
  /** The negative side, summed as a positive number. */
  moneyOut: number;
  asOfSeq: number;
};

export type SyncPullResponse = {
  /** Every version with server_seq > since, ascending. Includes non-current versions and tombstones. */
  entries: SyncEntryRow[];
  /** The full readable catalog, every pull — it is small and it is how a record another member added arrives. */
  records: RecordListItem[];
  /** Full schemas for records whose field_set_ver differs from what the device sent (or that it does not know). */
  schemas: RecordSchemaResponse[];
  aggregates: SyncAggregateRow[];
  /**
   * The day buckets, sent ONLY on the page that catches the device up — the one
   * whose `hasMore` is false.
   *
   * Not cursor bounded, and deliberately not sent per page. `as_of_seq` on an
   * aggregate is the record's LATEST write, so on a half-million-row fill every
   * bucket passes `as_of_seq > since` on every page and the cursor bound saves
   * nothing. Worse, a bucket is a total as of the horizon while a mid-fill
   * device holds only part of the rows, so buckets that arrive early are ahead
   * of the mirror they will be read beside. Sending them when the run is caught
   * up makes the two agree by construction.
   */
  buckets: SyncBucketRow[];
  /** The complete set of record ids the caller may open. The device drops local data for anything absent. */
  readableRecordIds: string[];
  /** The server_seq the server evaluated up to — NOT the max row sent. Rows in ungranted records are filtered out; this keeps the cursor moving past them. */
  nextSince: number;
  /** True when the entries page was truncated; call again with since = nextSince. */
  hasMore: boolean;
  /**
   * How many entry versions the caller may read after this page, up to the
   * same horizon. Present only when the request set `withRemaining`.
   */
  remaining?: number;
};

/** The one websocket message: a seq advanced. Never data. */
export type SyncEventMessage = { type: 'seq'; tenantId: string; seq: number };

/* -------------------------------------------------------------------------- */
/* Attachments                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The attachment contract. Three calls, in this order, and none of them carries
 * a byte of the file through this API.
 *
 *   POST /v1/attachments          ask for somewhere to put one file
 *   PUT  <the url that answered>  the phone sends the bytes to the store
 *   POST /v1/attachments/:id/commit   prove it landed, and claim it
 *
 * Only a committed attachment id may be written into an entry. An id that was
 * asked for and never committed is deleted by the store's own lifecycle rule,
 * so an abandoned upload costs nothing and leaves nothing behind.
 */

/** What the phone knows before it uploads. */
export type AttachmentCreateRequest = {
  /** Which record the file belongs to. Decides the caller's permission. */
  recordId: string;
  /** Which field will name it. Decides the type the template allows. */
  fieldId: string;
  /** What the person's device calls it. Shown in a row, never used as a key. */
  fileName: string;
  mimeType: string;
  /** What the phone measured. Checked again at commit against what landed. */
  sizeBytes: number;
};

export type AttachmentCreateResponse = {
  /** The id to commit, and later the id the entry stores. */
  id: string;
  /** PUT the bytes here. */
  uploadUrl: string;
  /** Send exactly these headers with the PUT. They are part of the signature. */
  uploadHeaders: Record<string, string>;
  /** ISO 8601. After this the link is refused and a new one must be asked for. */
  expiresAt: string;
};

/** One attachment, as every read of one answers. */
export type AttachmentSummary = {
  id: string;
  recordId: string;
  fieldId: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  /** The stored image's size in pixels. Null for anything that is not an image. */
  width: number | null;
  height: number | null;
  /** A short-lived link to the bytes. Null until the upload is committed. */
  readUrl: string | null;
  /**
   * A short-lived link to the small copy, for a list row. Null for a file with
   * no thumbnail, which today means a PDF.
   *
   * A row draws THIS and never `readUrl`: a list of thirty receipts must not
   * download thirty photographs.
   */
  thumbnailUrl: string | null;
  /** ISO 8601, when both links stop working. Null before the upload is proven. */
  readUrlExpiresAt: string | null;
};

export type AttachmentCommitResponse = { attachment: AttachmentSummary };

/** GET /v1/attachments?ids=a,b,c — what the entry form needs to draw its rows. */
export type AttachmentListResponse = { attachments: AttachmentSummary[] };

export type AttachmentDeleteResponse = { id: string; deleted: true };
